import { FiEdit2, FiTrash2, FiDownload, FiTag } from 'react-icons/fi';
import type { ColoringPageListItem } from '../../types';

interface ImageCardProps {
  image: ColoringPageListItem;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  onDownload?: (id: number) => void;
  onSelect?: (id: number) => void;
  isSelected?: boolean;
}

export function ImageCard({
  image,
  onEdit,
  onDelete,
  onDownload,
  onSelect,
  isSelected = false,
}: ImageCardProps) {
  const sourceTypeColors: Record<string, string> = {
    Upload: 'bg-gray-100 text-gray-700',
    PhotoConversion: 'bg-purple-100 text-purple-700',
    Cleanup: 'bg-green-100 text-green-700',
    WatermarkRemoval: 'bg-blue-100 text-blue-700',
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className={`
        group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg
        transition-all duration-200 cursor-pointer
        ${isSelected ? 'ring-2 ring-indigo-500' : ''}
      `}
      onClick={() => onSelect?.(image.id)}
    >
      <div className="aspect-square relative overflow-hidden bg-gray-100">
        <img
          src={image.thumbnailUrl}
          alt={image.title || image.originalFileName}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          loading="lazy"
        />

        {/* Source type badge */}
        <div className="absolute top-2 left-2">
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${sourceTypeColors[image.sourceType] || sourceTypeColors.Upload}`}>
            {image.sourceType === 'PhotoConversion' ? 'Line Art' :
             image.sourceType === 'WatermarkRemoval' ? 'Cleaned' :
             image.sourceType}
          </span>
        </div>

        {/* Action buttons overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(image.id); }}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-700 hover:bg-indigo-500 hover:text-white transition-colors"
              title="Edit"
            >
              <FiEdit2 className="w-5 h-5" />
            </button>
          )}
          {onDownload && (
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(image.id); }}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-700 hover:bg-green-500 hover:text-white transition-colors"
              title="Download"
            >
              <FiDownload className="w-5 h-5" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(image.id); }}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-700 hover:bg-red-500 hover:text-white transition-colors"
              title="Delete"
            >
              <FiTrash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-medium text-gray-900 truncate">
          {image.title || image.originalFileName}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {formatDate(image.createdDate)}
        </p>

        {image.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {image.tags.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
                style={tag.colorHex ? { backgroundColor: `${tag.colorHex}20`, color: tag.colorHex } : undefined}
              >
                <FiTag className="w-3 h-3" />
                {tag.name}
              </span>
            ))}
            {image.tags.length > 3 && (
              <span className="text-xs text-gray-400">+{image.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
